import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

import { getUrl } from "../../helpers/url";

import { fetchStatus, setShowErrorModal } from "./preferenceSlice";

const initialState = {
  shopifyCustomerNumberPreference: {
    value: "shopify",
  },
  isContactsSyncRulesSaved: false,
  isContactDefaultMappingsSaved: false,
  msbcFilterPreference: undefined,
  shopifyFilterPreference: undefined,
  msbcCustomerPreference: {
    name: "Customer Number",
    value: "customerNumber",
  },
  shopifyCustomerPreference: undefined,
  msbcCustomerDropdownFields: [
    {
      CRF: "customerNumber",
      HRF: "Customer Number",
    },
  ],
  shopifyCustomerNumberFields: [
    {
      title: "Let Business Central create customer number",
      value: "msbc",
    },
    {
      title: "Take customer number from Shopify",
      value: "shopify",
    },
  ],
  paymentTermPreference: undefined,
  currencyPreference: undefined,
  customerPostingGroupPreference: undefined,
  vatBusinessPostingGroupPreference: undefined,
  generalBusinessPostingGroupPreference: undefined,
  generalProductPostingGroupPreference: undefined,
  productTypePreference: undefined,
  msbcDefaultFields: {
    loading: false,
    msbcDefaultFields: {},
    error: "",
  },
};

export const fetchMsbcDefaultFields = createAsyncThunk(
  "contact/fetchMsbcDefaultFields",
  async ({ shop, tenantId }, { dispatch }) => {
    try {
      const { data: response } = await axios.get(
        getUrl("REACT_APP_GET_DEFAULT_MSBC_FIELDS"),
        {
          params: {
            shop,
            tenantId,
          },
        }
      );
      return response;
    } catch (error) {
      let errorMessage;
      if (error.response && error.response.data.message)
        errorMessage = error.response.data.message;
      else errorMessage = error.message;
      dispatch(setShowErrorModal({ message: errorMessage }));
      throw error;
    }
  }
);

export const contactSlice = createSlice({
  name: "contact",
  initialState,
  reducers: {
    onChangeShopifyCustomerNumber(state, { payload }) {
      state.shopifyCustomerNumberPreference = {
        value: payload.selectedOption,
      };
      if (state.shopifyCustomerNumberPreference.value === "shopify") {
        state.shopifyCustomerPreference = undefined;
      } else {
        state.shopifyCustomerPreference = {
          name: "Do not add it anywhere",
          value: "doNotAdd",
        };
      }

      state.isContactsSyncRulesSaved = false;
    },

    onChangeContactDropdownFields(state, { payload }) {
      if (payload.selectingFor === "msbcFilterDropdown") {
        state.msbcFilterPreference = {
          name: payload.selectedOption.HRF,
          value: payload.selectedOption.CRF,
        };
        state.isContactsSyncRulesSaved = false;
      } else if (payload.selectingFor === "shopifyFilterDropdown") {
        state.shopifyFilterPreference = {
          name: payload.selectedOption.HRF,
          value: payload.selectedOption.CRF,
        };
        state.isContactsSyncRulesSaved = false;
      } else if (payload.selectingFor === "shopifyCustomerNumber") {
        state.shopifyCustomerPreference = {
          name: payload.selectedOption.HRF,
          value: payload.selectedOption.CRF,
        };
        state.isContactsSyncRulesSaved = false;
      } else if (payload.selectingFor === "paymentTerms") {
        state.paymentTermPreference = {
          name: payload.selectedOption.title,
          value: payload.selectedOption.value,
        };
        state.isContactDefaultMappingsSaved = false;
      } else if (payload.selectingFor === "currency") {
        state.currencyPreference = {
          name: payload.selectedOption.title,
          value: payload.selectedOption.value,
        };
        state.isContactDefaultMappingsSaved = false;
      } else if (payload.selectingFor === "customerPostingGroup") {
        state.customerPostingGroupPreference = {
          name: payload.selectedOption.title,
          value: payload.selectedOption.value,
        };
        state.isContactDefaultMappingsSaved = false;
      } else if (payload.selectingFor === "vatPostingGroup") {
        state.vatBusinessPostingGroupPreference = {
          name: payload.selectedOption.title,
          value: payload.selectedOption.value,
        };
        state.isContactDefaultMappingsSaved = false;
      } else if (payload.selectingFor === "generalBusinessPostingGroup") {
        state.generalBusinessPostingGroupPreference = {
          name: payload.selectedOption.title,
          value: payload.selectedOption.value,
        };
        state.isContactDefaultMappingsSaved = false;
      } else if (payload.selectingFor === "generalProductPostingGroup") {
        state.generalProductPostingGroupPreference = {
          name: payload.selectedOption.title,
          value: payload.selectedOption.value,
        };
        state.isContactDefaultMappingsSaved = false;
      } else if (payload.selectingFor === "productType") {
        state.productTypePreference = {
          name: payload.selectedOption.title,
          value: payload.selectedOption.value,
        };
        state.isContactDefaultMappingsSaved = false;
      }
    },

    setIsContactSyncRulesAndDefaultMappings(state, { payload }) {
      if (payload.type === "syncRules") {
        state.isContactsSyncRulesSaved = true;
      } else if (payload.type === "defaultMappings") {
        state.isContactDefaultMappingsSaved = true;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchMsbcDefaultFields.pending, (state, action) => {
      if (!state.msbcDefaultFields.loading) {
        state.msbcDefaultFields.loading = true;
      }
    });
    builder.addCase(fetchMsbcDefaultFields.fulfilled, (state, action) => {
      if (state.msbcDefaultFields.loading) {
        state.msbcDefaultFields.loading = false;
        state.msbcDefaultFields.msbcDefaultFields = action.payload;
        state.msbcDefaultFields.error = "";
      }
    });
    builder.addCase(fetchMsbcDefaultFields.rejected, (state, action) => {
      if (state.msbcDefaultFields.loading) {
        state.msbcDefaultFields.loading = false;
        state.msbcDefaultFields.error = action.error.message ?? "";
        state.msbcDefaultFields.msbcDefaultFields = {};
      }
    });

    builder.addCase(fetchStatus.fulfilled, (state, action) => {
      const { preference } = action.payload;

      if (preference !== undefined && Object.keys(preference).length > 0) {
        const { customer } = preference;

        if (customer && Object.keys(customer).length > 0) {
          if (customer?.msbcCustomerNumber) {
            state.shopifyCustomerNumberPreference = {
              value: customer.msbcCustomerNumber.direction,
            };

            if (customer?.msbcCustomerNumber?.mapping) {
              state.shopifyCustomerPreference = customer?.msbcCustomerNumber
                ?.mapping?.shProperty?.value
                ? customer.msbcCustomerNumber.mapping.shProperty
                : {
                    name: "msbc customer number",
                    value: "msbccustomernumber",
                  };
            }
          }

          if (customer?.filterOptions) {
            state.shopifyFilterPreference = customer?.filterOptions
              ?.filterShProperty?.value
              ? customer.filterOptions.filterShProperty
              : undefined;

            state.msbcFilterPreference = customer?.filterOptions
              ?.filterMsbcProperty?.value
              ? customer.filterOptions.filterMsbcProperty
              : undefined;
          }

          if (customer?.msbcFields) {
            const {
              paymentTerms,
              currency,
              customerPostingGroup,
              vatBusinessPostingGroup,
              generalBusinessPostingGroup,
              generalProductPostingGroup,
              productTypes,
            } = customer.msbcFields;

            state.paymentTermPreference = paymentTerms?.value
              ? paymentTerms
              : undefined;
            state.currencyPreference = currency?.value ? currency : undefined;
            state.customerPostingGroupPreference = customerPostingGroup?.value
              ? customerPostingGroup
              : undefined;
            state.vatBusinessPostingGroupPreference =
              vatBusinessPostingGroup?.value
                ? vatBusinessPostingGroup
                : undefined;
            state.generalBusinessPostingGroupPreference =
              generalBusinessPostingGroup?.value
                ? generalBusinessPostingGroup
                : undefined;
            state.generalProductPostingGroupPreference =
              generalProductPostingGroup?.value
                ? generalProductPostingGroup
                : undefined;
            state.productTypePreference = productTypes?.value
              ? productTypes
              : undefined;
          }
        }
      }

      if (
        state.shopifyFilterPreference &&
        state.msbcFilterPreference &&
        state.shopifyCustomerPreference &&
        state.shopifyCustomerNumberPreference
      ) {
        state.isContactsSyncRulesSaved = true;
      }

      if (
        state.paymentTermPreference &&
        state.currencyPreference &&
        state.vatBusinessPostingGroupPreference &&
        state.customerPostingGroupPreference &&
        state.generalBusinessPostingGroupPreference &&
        state.generalProductPostingGroupPreference &&
        state.productTypePreference
      ) {
        state.isContactDefaultMappingsSaved = true;
      }
    });
  },
});

export const {
  onChangeShopifyCustomerNumber,
  onChangeContactDropdownFields,
  setIsContactSyncRulesAndDefaultMappings,
} = contactSlice.actions;

export default contactSlice.reducer;
